import { PageHero } from '@components/common';
import { Shield, Target, Users, Award, ArrowRight, Clock, Linkedin } from 'lucide-react';
import { CALENDLY_URL } from '@constants';

export default function AboutPage() {
  // AboutPage structured data for SEO
  const aboutStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About The CISO Network',
    description: 'Learn about The CISO Network - the leading provider of fractional CISO services.',
    url: 'https://thecisonetwork.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: 'The CISO Network',
      description:
        'The leading provider of fractional CISO services helping businesses build world-class security programs.',
      url: 'https://thecisonetwork.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutStructuredData) }}
      />
      <style>{`
        .perspective {
          perspective: 1000px;
        }
        .flip-card {
          perspective: 1000px;
        }
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transition: transform 0.5s;
          transform-style: preserve-3d;
        }
        .flip-card:hover .flip-card-inner {
          transform: rotateY(180deg);
        }
        .flip-card-front,
        .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          overflow: hidden;
          backface-visibility: hidden;
          box-shadow: 0 0 0 2px white, 0 0 0 4px hsl(180 55% 38%);
        }
        .flip-card-front {
          background-color: #fff;
        }
        .flip-card-back {
          background-color: hsl(180 55% 38%);
          transform: rotateY(180deg);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
      <PageHero
        title="About Us"
        subtitle="Building the Future of Security Leadership"
        eyebrow="About"
        seoTitle="About Us | The CISO Network"
        seoDescription="Learn about The CISO Network - the leading provider of fractional CISO services helping businesses build world-class security programs."
      />

      {/* Our Story */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Founded by two experts in building high performing teams within cyber security,
                  The CISO Network emerged from a shared realisation: there was a gap in the market
                  for a better model, one where businesses could access strategic security expertise
                  without the cost and risk of full-time hires and where CISOs could work flexibly
                  and gain valuable experience faster.
                </p>
                <p>
                  We believe this model is the future of security leadership. By connecting
                  businesses with a network of experienced fractional CISOs, we are helping to build
                  a world where every organisation can access the expertise they need to protect
                  their most valuable assets and where security leaders can thrive in fulfilling
                  careers.
                </p>
                <p>
                  We work with clients across diverse industries, helping them navigate complex
                  regulatory landscapes, respond to incidents, and build security cultures that
                  protect their businesses and customers.
                </p>
                <p>
                  And should the day come that hiring a full-time CISO is the right solution for
                  your business, we will tell you, readily support you in finding the right person
                  and in ensuring a smooth transition.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-6 rounded-xl bg-background">
                <div className="text-xl font-heading font-bold text-foreground mb-2">
                  The UK #1 network of Fractional CISOs
                </div>
                <div className="text-sm text-muted-foreground">
                  Our CISOs bring decades of experience from FTSE 100 companies through to
                  high-growth startups.
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background">
                <div className="text-xl font-heading font-bold text-foreground mb-2">
                  Rapid Deployment
                </div>
                <div className="text-sm text-muted-foreground">
                  Typical onboarding within two weeks, getting you protected faster.
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background">
                <div className="text-xl font-heading font-bold text-foreground mb-2">
                  Cost Effective
                </div>
                <div className="text-sm text-muted-foreground">
                  Significant savings compared to full-time CISO hires without compromising on
                  expertise.
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background">
                <div className="text-xl font-heading font-bold text-foreground mb-2">
                  Client Focused
                </div>
                <div className="text-sm text-muted-foreground">
                  High client retention through exceptional service and measurable results.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-secondary tracking-wide uppercase">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-3">
              What We Stand For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Security First',
                description: 'We believe security should be a business enabler, not a blocker.',
              },
              {
                icon: Target,
                title: 'Pragmatic',
                description: 'Real-world solutions that work for your business context.',
              },
              {
                icon: Users,
                title: 'Collaborative',
                description: 'We work alongside your team, not above it.',
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'Only the most experienced CISOs make it into our network.',
              },
            ].map((value, i) => (
              <div key={i} className="text-center group">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5 group-hover:glow-teal transition-shadow duration-300">
                  <value.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-secondary tracking-wide uppercase">
              Leadership
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mt-3">
              Meet Our Founders
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {[
              {
                name: 'Chris Dunning-Walton',
                role: 'CEO',
                bio: "Board-level cybersecurity advisor and exited founder with 15+ years' experience building cyber businesses, placing senior security leaders, and connecting scaleups with investors.",
                image: '/images/team/ceo.jpg',
                linkedin: 'https://www.linkedin.com/in/chrisdw',
              },
              {
                name: 'Richard Midwinter',
                role: 'CTO',
                bio: "Technical leader with 20+ years' experience securing mission-critical systems across government, media and healthcare, including for the National Cyber Security Centre.",
                image: '/images/team/cto.jpg',
                linkedin: 'https://www.linkedin.com/in/r-i-c-h-a-r-d',
              },
            ].map((person, i) => (
              <div key={i} className="text-center">
                {/* Flip card */}
                <a
                  href={person.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-24 h-24 mx-auto mb-4 perspective flip-card"
                >
                  <div className="flip-card-inner">
                    {/* Front */}
                    <div className="flip-card-front">
                      <img
                        src={person.image}
                        alt={person.name}
                        className="w-full h-full object-cover grayscale"
                      />
                    </div>
                    {/* Back */}
                    <div className="flip-card-back">
                      <Linkedin className="h-10 w-10 text-white" />
                    </div>
                  </div>
                </a>
                <h3 className="font-heading font-semibold text-foreground">{person.name}</h3>
                <p className="text-secondary text-sm mb-2">{person.role}</p>
                <p className="text-muted-foreground">{person.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon - CISO Network */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 border border-teal/30 mb-8">
              <Clock className="h-4 w-4 text-teal" />
              <span className="text-sm font-medium text-teal">Coming Soon</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Meet the CISOs
            </h2>
            <p className="text-muted-foreground mb-6">
              We are incredibly proud of the experienced security leaders in our network. Each
              brings decades of hands-on experience, board-level credibility, and a track record of
              building effective security programmes across diverse industries.
            </p>
            <p className="text-muted-foreground mb-12">
              Soon we will be introducing you to some of the individuals who make up The CISO
              Network - seasoned professionals who have led security at FTSE100 companies,
              high-growth startups, and everything in between. Watch this space.
            </p>
          </div>

          {/* CISO placeholders */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="text-center mb-4">
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4 ring-2 ring-border ring-offset-2 ring-offset-background bg-muted flex items-center justify-center">
                  <svg
                    className="w-16 h-16 text-muted-foreground/40"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
                <div className="space-y-2">
                  <div className="h-5 bg-muted rounded w-3/4 mx-auto" />
                  <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
                  <div className="h-3 bg-muted rounded w-full mx-auto mt-3" />
                  <div className="h-3 bg-muted rounded w-5/6 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Want to Learn More?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Book a discovery call to discuss your security needs and see how The CISO Network can
            help your organisation.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
